import { ITableProps } from 'components/curo-components/common/Table/Table';
import { CuroApplicationVariables, AvailableOffersTableVariables } from 'enums/curo/Variables';
import { TableValue } from 'product_modules/api/Types';
import { ICuroApplication } from 'types/curo/CuroApplication';
import { formatToMonetary, formatToMonth, formatToPercent } from 'utils/curo-helpers/formatters';

export interface ILoanOfferItem {
  [offerId: string]: {
    productName: string;
    loanOfferData: {
      loanAmount: number;
      monthlyPayment: number;
      apr: number;
      termInMonths: number;
    };
  };
}

export interface IRefinanceLoanOfferItem {
  [offerId: string]: {
    productName: string;
    loanOfferData: {
      amountFinanced: number;
      apr: number;
      termInMonths: number;
      monthlyPayment: number;
      financeCharge: number;
      totalPayment: number;
      cashToCustomer: number;
      payoffAmount: number;
    };
  };
}

type LoanOfferDataKeys = keyof ILoanOfferItem[string]['loanOfferData'] | keyof IRefinanceLoanOfferItem[string]['loanOfferData'];

export const loanOfferLabels: Record<LoanOfferDataKeys, string> = {
  loanAmount: 'Loan Amount',
  amountFinanced: 'Amount Financed',
  apr: 'APR',
  termInMonths: 'Term',
  monthlyPayment: 'Monthly Payment ',
  financeCharge: 'Finance Charge',
  totalPayment: 'Total of Payments',
  cashToCustomer: 'Amount Paid To You',
  payoffAmount: 'Amount Paid On Existing Loan',
};

export const SL_UNSECURED_LOAN_TYPE = 'SL Unsecured';
export const SL_REFI_LOAN_TYPE = 'SL Refi';

export const loanOfferValue: Record<LoanOfferDataKeys, (value: number) => string> = {
  amountFinanced: (value) =>
    formatToMonetary(value, {
      minimumFractionDigits: 2,
    }),
  loanAmount: (value) =>
    formatToMonetary(value, {
      minimumFractionDigits: 2,
    }),
  apr: (value) =>
    formatToPercent(value, {
      minimumFractionDigits: 2,
    }),
  termInMonths: (value) => formatToMonth(value),
  monthlyPayment: (value) =>
    formatToMonetary(value, {
      minimumFractionDigits: 2,
    }),
  financeCharge: (value) =>
    formatToMonetary(value, {
      minimumFractionDigits: 2,
    }),
  totalPayment: (value) =>
    formatToMonetary(value, {
      minimumFractionDigits: 2,
    }),
  payoffAmount: (value) =>
    formatToMonetary(value, {
      minimumFractionDigits: 2,
    }),
  cashToCustomer: (value) =>
    formatToMonetary(value, {
      minimumFractionDigits: 2,
    }),
};

const formatRowValueToLoanOfferItem = (rowValue: TableValue[number]): ILoanOfferItem => {
  return {
    [rowValue._id]: {
      productName: rowValue[AvailableOffersTableVariables.ProductName] as string,
      loanOfferData: {
        loanAmount: rowValue[AvailableOffersTableVariables.LoanAmount] as number,
        monthlyPayment: rowValue[AvailableOffersTableVariables.MonthlyPayment] as number,
        apr: rowValue[AvailableOffersTableVariables.Apr] as number,
        termInMonths: rowValue[AvailableOffersTableVariables.TermInMonths] as number,
      },
    },
  };
};

const formatRefinanceRowValueToLoanOfferItem = (rowValue: TableValue[number]): IRefinanceLoanOfferItem => {
  return {
    [rowValue._id]: {
      productName: rowValue[AvailableOffersTableVariables.ProductName] as string,
      loanOfferData: {
        amountFinanced: rowValue[AvailableOffersTableVariables.LoanAmount] as number,
        apr: rowValue[AvailableOffersTableVariables.Apr] as number,
        termInMonths: rowValue[AvailableOffersTableVariables.TermInMonths] as number,
        monthlyPayment: rowValue[AvailableOffersTableVariables.MonthlyPayment] as number,
        financeCharge: rowValue[AvailableOffersTableVariables.FinanceCharge] as number,
        totalPayment: rowValue[AvailableOffersTableVariables.TotalPayment] as number,
        payoffAmount: rowValue[CuroApplicationVariables.PayoffAmount] as number,
        cashToCustomer: rowValue[CuroApplicationVariables.CashToCustomer] as number,
      },
    },
  };
};

export const parseLoanOffersToLoanOfferItem = (
  variables: ICuroApplication['variables'],
): ILoanOfferItem => {
  const loanOffersVariable = variables[CuroApplicationVariables.AvailableLoanOffers] as TableValue;
  const slUnsecuredLoanOffer = loanOffersVariable.filter(loan => loan[AvailableOffersTableVariables.LoanType] === SL_UNSECURED_LOAN_TYPE);

  return slUnsecuredLoanOffer.reduce((acc, cur) => {
    const formattedRowValue = formatRowValueToLoanOfferItem(cur);

    return { ...acc, ...formattedRowValue };
  }, {});
};

export const parseRefinanceLoanOffersToLoanOfferItem = (
  variables: ICuroApplication['variables'],
): IRefinanceLoanOfferItem => {
  const loanOffersVariable = variables[CuroApplicationVariables.AvailableLoanOffers] as TableValue;
  const slRefiLoanOffer = loanOffersVariable.filter(loan => loan[AvailableOffersTableVariables.LoanType] === SL_REFI_LOAN_TYPE);
  const payoffAmount = variables[CuroApplicationVariables.PayoffAmount] as number;
  const cashToCustomer = variables[CuroApplicationVariables.CashToCustomer] as number;

  return slRefiLoanOffer.reduce((acc, cur) => {
    const formattedRowValue = formatRefinanceRowValueToLoanOfferItem({
      ...cur,
      [CuroApplicationVariables.PayoffAmount]: payoffAmount,
      [CuroApplicationVariables.CashToCustomer]: cashToCustomer,
    });

    return { ...acc, ...formattedRowValue};
  }, {});
};

export const formatLoanOfferDataToTableData = (
  offerData: ILoanOfferItem[string]['loanOfferData'] | IRefinanceLoanOfferItem[string]['loanOfferData'],
  origGpsLoanOrAccountNumber?: string,
): ITableProps['data'] => {
  return (Object.entries(offerData) as Array<[LoanOfferDataKeys, number]>).map(([key, value]) => {
    const valueFontWeight = key === 'financeCharge' || key === 'apr' ? 'bold' : 'normal';
    const labelFontWeight = key === 'financeCharge' || key === 'apr' ? 'bold' : 'normal';
    const label = key === 'payoffAmount' && origGpsLoanOrAccountNumber 
      ? `${loanOfferLabels[key]}: ${origGpsLoanOrAccountNumber}` 
      : loanOfferLabels[key];

    return {
      label: label,
      value: loanOfferValue[key](value),
      valueFontWeight,
      labelFontWeight,
    };
  });
};

export const getTableColumns = (tableLength: number): number => (tableLength === 1 ? 2 : 1);

export const getTableRows = (isSLRefi: boolean): number => (isSLRefi ? 4 : 2);
